import React, { useState, useEffect } from 'react';
import { Card, List, Typography, Space, Empty, Spin, Tag, Progress } from 'antd';
import { CheckCircleOutlined, CalendarOutlined, BookOutlined, FieldTimeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getCompletedAssessments } from '../services/trainingService';
import type { Training } from '../types/training';

const { Title, Text } = Typography;

const CompletedAssessments: React.FC = () => {
  const [assessments, setAssessments] = useState<Training[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadCompletedAssessments();
  }, []);

  const loadCompletedAssessments = async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await getCompletedAssessments();
      setAssessments(data);
    } catch (error) {
      console.error('Error loading completed assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div className="assessment-header">
        <Title level={2}>Completed Training Programs</Title>
        <Text type="secondary">
          Review your completed training assessments
        </Text>
      </div>

      {assessments.length === 0 ? (
        <Empty
          description="No completed training programs"
          style={{ marginTop: '48px' }}
        />
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
          dataSource={assessments}
          renderItem={(assessment) => (
            <List.Item>
              <Card
                title={assessment.trainingName}
                extra={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text type="secondary">Training ID: </Text>
                    <Text strong>{assessment.trainingId}</Text>
                  </div>

                  {assessment.trainingLink && (
                    <div>
                      <BookOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      <a href={assessment.trainingLink} target="_blank" rel="noopener noreferrer">
                        Access Training Resources
                      </a>
                    </div>
                  )}

                  {assessment.estimatedEffortHours && (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <FieldTimeOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                      <Text>{assessment.estimatedEffortHours} hours</Text>
                    </div>
                  )}

                  {assessment.percentage !== undefined && (
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">Score: </Text>
                      <Progress
                        percent={assessment.percentage}
                        size="small"
                        strokeColor={assessment.percentage >= (assessment.passingPercentage ?? 80) ? '#52c41a' : '#faad14'}
                      />
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">
                      <CalendarOutlined style={{ marginRight: '8px' }} />
                      Completed: {assessment.completedDate && dayjs(assessment.completedDate.split('T')[0]).format('DD/MM/YYYY')}
                    </Text>
                    <Tag color="green">Passed</Tag>
                  </div>
                </Space>
              </Card>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

export default CompletedAssessments;